import React from "react";

interface FarmerDetailsProps {
  data: Record<string, any>;
  onClose: () => void;
}

const formatLabel = (label: string) =>
  label
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (l) => l.toUpperCase());

const contactFields = ["farmerContactNumber", "alternateContactNumber", "email"];
const personalFields = ["farmerName", "gender", "dateOfBirth", "age"];
const addressFields = ["village", "tehsilMandal", "district", "state", "pinCode"];
// Any other fields will go into "Other Details"

const FarmerDetails: React.FC<FarmerDetailsProps> = ({ data, onClose }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="text-center text-gray-500 py-8 flex flex-col items-center">
        <p>No data available.</p>
      </div>
    );
  }

  const contactDetails = contactFields.filter((f) => data[f] !== undefined);
  const personalDetails = personalFields.filter((f) => data[f] !== undefined && f !== "farmerName");
  const addressDetails = addressFields.filter((f) => data[f] !== undefined);
  const otherDetails = Object.keys(data).filter(
    (key) =>
      !contactFields.includes(key) &&
      !personalFields.includes(key) &&
      !addressFields.includes(key)
  );

  return (
    <div className="space-y-4">
      {/* Contact Details */}
      {contactDetails.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-300/10 p-3 rounded-md">
          <div className="text-md font-semibold 
           text-gray-700 dark:text-gray-200 
           mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
            Contact Details
          </div>
          <div className="grid grid-cols-1 gap-1">
            {contactDetails.map((key) => (
              <div key={key} className="flex flex-row items-center py-1 border-b border-gray-100 last:border-0">
                <span className="w-1/2 text-sm font-medium 
               text-gray-600 dark:text-gray-300">{formatLabel(key)}:</span>
                <span className="w-1/2 text-sm font-medium 
               text-gray-600 dark:text-gray-300">{data[key] !== undefined && data[key] !== null && data[key] !== "" ? data[key] : "-"}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Personal Details */}
      {personalDetails.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-300/10 p-3 rounded-md">
          <div className="text-md font-semibold 
           text-gray-700 dark:text-gray-200 
           mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            Personal Details
          </div>
          <div className="grid grid-cols-1 gap-1">
            {personalDetails.map((key) => (
              <div key={key} className="flex flex-row items-center py-1 border-b border-gray-100 last:border-0">
                <span className="w-1/2 text-sm font-medium text-gray-600  text-gray-600 dark:text-gray-300">{formatLabel(key)}:</span>
                <span className="w-1/2 text-sm text-gray-900 font-medium  text-gray-600 dark:text-gray-300">{data[key] !== undefined && data[key] !== null && data[key] !== "" ? data[key] : "-"}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Address Details */}
      {addressDetails.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-300/10 p-3 rounded-md">
          <div className="text-md font-semibold 
           text-gray-700 dark:text-gray-200 
           mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            Address Details
          </div>
          <div className="grid grid-cols-1 gap-1">
            {addressDetails.map((key) => (
              <div key={key} className="flex flex-row items-center py-1 border-b border-gray-100 last:border-0">
                <span className="w-1/2 text-sm font-medium text-gray-600  text-gray-600 dark:text-gray-300">{formatLabel(key)}:</span>
                <span className="w-1/2 text-sm text-gray-900 font-medium  text-gray-600 dark:text-gray-300">{data[key] !== undefined && data[key] !== null && data[key] !== "" ? data[key] : "-"}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Other Details */}
      {otherDetails.length > 0 && (
        <section className="bg-gray-50 dark:bg-gray-300/10 p-3 rounded-md">
          <div className="text-md font-semibold 
           text-gray-700 dark:text-gray-200 
           mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            Other Details
          </div>
          <div className="grid grid-cols-1 gap-1">
            {otherDetails.map((key) => (
              <div key={key} className="flex flex-row items-center py-1 border-b border-gray-100 last:border-0">
                <span className="w-1/2 text-sm font-medium text-gray-600  text-gray-600 dark:text-gray-300">{formatLabel(key)}:</span>
                <span className="w-1/2 text-sm text-gray-900 font-medium  text-gray-600 dark:text-gray-300">{data[key] !== undefined && data[key] !== null && data[key] !== "" ? data[key] : "-"}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default FarmerDetails;
